import { PlatformEvent } from 'klayowebshared';
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { PLATFORM_ENTERED_KEY } from '../constants';
import { Organization } from '../data/Organization';
import { CurrentUser } from '../data/users/CurrentUser';

/**
 * Foundational PostHog wiring. Once the authenticated user and organization are loaded,
 * identify the person and associate them with their organization group (both idempotent,
 * refreshed whenever the data changes), and fire platform:entered once — the first time
 * the user enters the app this session. Runs regardless of route — onboarding or landing.
 * In preview mode this is a no-op because capturing is opted out at bootstrap.
 *
 * The person is skipped while impersonating (same rule as App.identifyUserpilot): CurrentUser is
 * built from activeUser, so `email`/`fullName` are the IMPERSONATED employee's, and identifying
 * would $set a support session over that named person's profile. The org group still applies —
 * it isn't personal, and dropping it would blank impersonated activity from every org breakdown.
 */
export function useAnalyticsIdentity(
  user: CurrentUser | undefined,
  organization: Organization | undefined
): void {
  useEffect(() => {
    if (!user?.employeeId || !user?.email || !organization?.organizationId) return;

    if (!user.impersonating) {
      // Key the person by email so downstream reporting (HubSpot) resolves to the right contact.
      posthog.identify(user.email, {
        $email: user.email,
        $name: user.fullName,
        email: user.email,
        // Limited-access (non-full) user — inverse of isFullAccess.
        IsLimited: !user.isFullAccess,
        hasEnrolments: !!user.hasActiveEnrollments,
        hasSkills: !!user.hasSkills,
        unlockedFreeJob: !!user.unlockedFreeJob,
        unlockedAnyJob: !!user.unlockedAnyJob,
        hasPrepaidBalance: !!user.hasPrepaidBalance,
        currentRole: user.currentRoleStandardLibraryJobName,
        careerRole: user.careerRole,
        profileJob: user.displayJobTitle || user.jobTitle,
        isTeamManager: !!user.isTeamLead,
        isInstructor: !!user.isInstructor,
        isAdmin: !!user.isOrganizationAdmin,
        isTrainingManager: !!user.isTrainingManager,
      });
    }

    posthog.group('organization', organization.organizationId, {
      orgName: organization.name,
      isFreeOrg: !organization.isPaidPlan,
      hasCore: organization.isEnabledCoreFeatures,
      hasTrainingPlus: !!organization.enabledFeatures?.Training,
      hasIntelligence: !!organization.enabledFeatures?.KlayoIntelligence,
    });

    if (!user.impersonating && !sessionStorage.getItem(PLATFORM_ENTERED_KEY)) {
      posthog.capture(PlatformEvent.Entered);
      sessionStorage.setItem(PLATFORM_ENTERED_KEY, '1');
    }
  }, [user, organization]);
}
